import { api } from './client';

export interface InventoryItemDto {
  id: number;
  name: string;
  code: string;
  barcode: string | null;
  unit: string;
  unitPrice: number;
  quantityOnHand: number;
  reservedQuantity: number;
  availableQuantity: number;
  inventoryAccount: number;
  cogsAccount: number;
  incomeAccount: number;
  vatRate: number;
  defaultSupplierId: number | null;
  reorderLevel: number;
  warehouse: string | null;
  valuationMethod: string;
  description: string | null;
  type: string;
  isObsolete: boolean;
}

export interface StockMovementDto {
  id: number;
  inventoryItemId: number;
  movementType: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  reason: string;
  referenceDocument: string | null;
  movementDate: string;
}

export interface CreateInventoryItemRequest {
  name: string;
  code: string;
  unit: string;
  unitPrice: number;
  inventoryAccount: number;
  cogsAccount: number;
  incomeAccount: number;
  vatRate: number;
  barcode?: string;
  defaultSupplierId?: number | null;
  reorderLevel?: number;
  warehouse?: string;
  description?: string;
}

export interface UpdateInventoryItemRequest {
  name: string;
  code: string;
  unit: string;
  unitPrice: number;
  inventoryAccount: number;
  cogsAccount: number;
  incomeAccount: number;
  vatRate: number;
}

export const inventoryApi = {
  list: (params: { search?: string; type?: string; limit?: number } = {}) =>
    api.get<InventoryItemDto[]>('/inventory', params),

  getById: (id: number) =>
    api.get<InventoryItemDto>(`/inventory/${id}`),

  create: (data: CreateInventoryItemRequest) =>
    api.post<number>('/inventory', data),

  update: (id: number, data: UpdateInventoryItemRequest) =>
    api.put<void>(`/inventory/${id}`, data),

  delete: (id: number) =>
    api.delete<void>(`/inventory/${id}`),

  adjustStock: (id: number, quantity: number, reason: string) =>
    api.post<void>(`/inventory/${id}/adjust-stock`, { quantity, reason }),

  getMovements: (id: number) =>
    api.get<StockMovementDto[]>(`/inventory/${id}/movements`),
};

// ── Phase 2 read mirrors (2026-09-12): legacy warehouses + stock over
// imported goods_operations. Read-only, works on mirror workspaces.

export interface WarehouseDto {
  id: number;
  name: string;
  description: string | null;
  location: string | null;
  isProduction: boolean;
  isObsolete: boolean;
  warehouseAccount: number;
}

export interface StockSummaryRow {
  goodsId: number;
  name: string;
  unit: string;
  code: string | null;
  quantity: number;
  value: number;
  warehouses: { warehouse: string; quantity: number }[];
}

export interface StockSummaryDto {
  items: StockSummaryRow[];
  totals: { itemCount: number; withStock: number; totalValue: number };
}

export interface TurnoverRow {
  goodsId: number;
  name: string;
  unit: string;
  opening: number;
  inflow: number;
  outflow: number;
  closing: number;
}

export interface TurnoverDto {
  from: string;
  to: string;
  rows: TurnoverRow[];
}

export const inventoryMirrorApi = {
  warehouses: () => api.get<WarehouseDto[]>('/inventory/warehouses'),

  stockSummary: () => api.get<StockSummaryDto>('/inventory/stock-summary'),

  turnover: (from: string, to: string) =>
    api.get<TurnoverDto>('/inventory/turnover', { from, to }),
};
